import type { AISettings } from './ai';
import type { ProposalHeading } from './proposal';

export interface ContextReference {
  fileName: string;
  path: string;
  tokenSize: number;
  summary: string;
  addedAt: string | Date;
  source: string;
}

export interface RecommendedTeamMember {
  userId?: string;
  teamId?: string;
  displayName: string;
  email?: string;
  matchScore: number;
  specialization: string;
  region: string;
  role?: string;
  includeInDraft: boolean;
  cvFileUrl?: string;
}

export interface RecommendedPartner {
  partnerId: string;
  userId?: string;
  contactEmail?: string;
  companyName: string;
  matchScore: number;
  combinedAlignmentScore: number;
  valueAdd: string;
  linkStatus?: 'pending' | 'accepted' | 'approved' | 'rejected';
  logoUrl?: string;
}

export interface Tender {
  id?: string;
  clientId?: string;
  clientName?: string;
  tenderId?: string;
  title: string;
  description: string;
  status: TenderStatus;
  adminOnly?: boolean;
  createdBy: string;
  createdAt: Date;
  updatedAt?: Date;
  deadline: Date;
  budget?: number;
  currency?: string;
  category?: string;
  attachments?: Attachment[];
  companyId: string;
  teamId?: string;
  teamMembers?: RecommendedTeamMember[];
  bidders?: Bidder[];
  winner?: string;
  requirements?: string[];
  evaluation_criteria?: EvaluationCriteria[];
  libraryDocOverrides?: Record<string, boolean | { isActive: boolean; activatedBy?: string }>;
  tenderIssuer?: string;
  noFiles?: boolean;
  keywords?: string[] | string;
  submissionDeadline?: string;
  deadlineDate?: string;
  ownerId?: string;
  ownerName?: string;
  teamName?: string;
  sourceStoreTenderId?: string;
  opportunityID?: string;
  isComplex?: boolean;
  isGrant?: boolean; // INTERIM WHITE-LABELING: isGrant
  draftResponse?: {
    status: string;
    progress?: number;
    startedAt?: Date | string;
    lastUpdated?: Date | string;
  };
  score?: number;
  partnerRelationship?: string;
  daysUntilDeadline?: number;
  analysisFileLink?: string;
  capabilityPitchPptxLink?: string;
  capabilityPitchHtmlLink?: string;
  capabilityPitchPdfLink?: string;
  capabilityPitchStatus?: string;
  capabilityPitchError?: string;
  capabilityPitchStartedAt?: string;
  draftFileLink?: string;
  reviewFileLink?: string;
  alignmentAnalysis?: {
    status: string;
    progress?: number;
    startedAt?: Date | string;
    lastUpdated?: Date | string;
    recommendedPartners?: RecommendedPartner[];
    recommendedTeamMembers?: RecommendedTeamMember[];
  };
  reviewResponse?: {
    status: string;
    startedAt?: Date | string;
    lastUpdated?: Date | string;
    lastType?: 'quick' | 'final';
  };
  compileStatus?: {
    status: string;
    startedAt?: Date | string;
    lastUpdated?: Date | string;
    error?: string;
    completedAt?: Date | string;
  };
  compiledFileLink?: string;
  presentationLink?: string;
  presentationError?: string;
  pdfLink?: string;
  pdfError?: string;
  isGeneratingPresentation?: boolean;
  isGeneratingPdf?: boolean;
  quickSettings?: AISettings & {
    enabled: boolean;
    structureAdherence?: 'strict' | 'flexible_headings' | 'fully_flexible';
  };
  // Template & Structure
  templateIds?: {
    draft?: string;
    cv?: string;
    case_study?: string;
  };
  draftTemplateMergeFields?: string[];
  customProposalStructure?: ProposalHeading[];
  extractedStructure?: ProposalHeading[];
  hybridExtractedStructure?: ProposalHeading[];
  anchored_forms?: {
    path: string;
    filename: string;
    id?: string;
    metrics?: {
      totalTags: number;
      filledTags: number;
      emptyTags: number;
    };
  }[];
  qtips?: string | string[];
  qtipsCompleted?: number[];
  projectDuration?: string;
  expectedStartDate?: string;
  contactPersonName?: string;
  contactPersonEmail?: string;
  scope?: string;
  tenderSourceURL?: string;
  llmProcessingTimestamp?: string | Date;
  dateUpdated?: string | Date;
  summary?: string;
  estimatedValue?: string;
  location?: string;
  state?: string;
  lastResetTimestamp?: Date;
  // Usage Counters
  draftCount?: number;
  reportCount?: number;
  reviewCount?: number;
  adhocDocCount?: number;
  cvCount?: number;
  sectionChatCount?: number;
  userDocsCount?: number;
  structureConfirmed?: boolean;
  supportDocsCompleted?: boolean;
  archiveReason?: string;
  archived?: boolean;
  includeInMetrics?: boolean;
  bidCost?: number;
  proposedFee?: number;
  tenderValue?: number;
  bidType?: string;
  lossReason?: string;
  actualRevenue?: number | string;
  debriefNotes?: string;
  performanceScore?: number | string;
  archivedDate?: Date | string;
  pursuitStatus?: 'watching' | 'pursuing';
  sharedWith?: string[];
  contributors?: SectionUserRef[];
  memberPermissions?: Record<string, string[]>;
  /** @deprecated Removed in Unified Encryption Architecture (Phase 3) */
  encryptionMode?: unknown;
  /** @deprecated Removed in Unified Encryption Architecture (Phase 3) */
  wrappedKey?: unknown;
  /** @deprecated Removed in Unified Encryption Architecture (Phase 3) */
  keyIV?: unknown;
  contextReferences?: ContextReference[]; // "Tender Briefcase" (Pinned context)
  prioritizedCollections?: string[]; // Array of CollectionArchetypes (e.g. ['cv', 'proposal'])
  detectedForms?: string[];
  detectedFormLinks?: Record<string, string>;
}

export enum TenderStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  CLOSED = 'closed',
  AWARDED = 'awarded',
  CANCELLED = 'cancelled',
  CONVERSION_ERROR = 'Conversion Error',
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  uploadedAt: Date;
  uploadedBy: string;
}

export interface Bidder {
  id: string;
  companyId: string;
  clientName: string;
  bidAmount: number;
  submittedAt: Date;
  status: BidStatus;
  documents?: Attachment[];
  notes?: string;
}

export type BidStatus = 'pending' | 'accepted' | 'rejected' | 'withdrawn';

export interface EvaluationCriteria {
  name: string;
  weight: number;
  description?: string;
}

export interface TenderFilter {
  status?: TenderStatus;
  category?: string;
  minBudget?: number;
  maxBudget?: number;
  startDate?: Date;
  endDate?: Date;
  companyId?: string;
}

export interface SectionUserRef {
  uid: string;
  displayName: string;
  email?: string;
  at?: Date | string;
}

export interface Section {
  id: string;
  title: string;
  content: string;
  index: number;
  status?: 'pending' | 'in_progress' | 'complete' | 'reviewed';
  encrypted_content?: string;
  wordCount?: number;
  suggested_content?: string | undefined;
  encrypted_suggested_content?: string | undefined;
  cross_boundary_suggestion?: boolean | undefined;
  suggested_by?: string | undefined;
  level?: number;
  updatedAt?: Date | string;
  sources?: string[];

  // Enterprise Fields
  lockedBy?: SectionUserRef;
  lastEditedBy?: SectionUserRef;
  reviewedBy?: SectionUserRef;
  assignedTo?: SectionUserRef;
  editHistory?: SectionUserRef[];
}

export interface SectionRevision {
  id: string;
  sectionId: string;
  encrypted_content: string;
  updatedAt: Date | string;
  editor: SectionUserRef;
  revisionNumber?: number;
}
